import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { closeDropdownFromElementWithin } from '#/components/Dropdown/closeDropdownFromElementWithin';
import { useKeyedState } from '#/hooks/useKeyedState';
import type { SearchType } from '#/modules/search/types/SearchType';

import {
  DesktopSearchBarFieldsHolder,
  DesktopSearchBarInputsForm,
} from './DesktopSearchBar.styled';
import {
  DesktopSearchBarContext,
  type DesktopSearchBarContextValue,
} from './DesktopSearchBarContext';
import type { DesktopSearchBarLocationInput } from './DesktopSearchBarLocationInput';
import type { DesktopSearchBarQueryInput } from './DesktopSearchBarQueryInput';
import { DesktopSearchBarSearchTypeSelect } from './DesktopSearchBarSearchTypeSelect';
import { DesktopSearchBarSubmitButton } from './DesktopSearchBarSubmitButton';

type Props = {
  defaultSearchType: SearchType;
  onSearch: (data: {
    listingType: SearchType;
    query: string;
    radius: string;
    triggeredByRemoteLocationChange: boolean;
  }) => void;

  // elements
  searchTypeSelect: ReactElement<
    typeof DesktopSearchBarSearchTypeSelect
  > | null;
  queryInput: ReactElement<typeof DesktopSearchBarQueryInput>;
  locationInput: ReactElement<typeof DesktopSearchBarLocationInput>;
  radiusInput: ReactNode;
  submitButton: ReactElement<typeof DesktopSearchBarSubmitButton>;
};

export function DesktopSearchBar({
  defaultSearchType,
  searchTypeSelect,
  queryInput,
  locationInput,
  radiusInput,
  submitButton,
  onSearch,
}: Props) {
  const formRef = useRef<HTMLFormElement>(null);
  const submit = useCallback(
    (options: { triggeredByRemoteLocationChange: boolean }) => {
      const form = formRef.current;
      if (!form) return;

      const formData = new FormData(form);
      onSearch({
        query: formData.get('q') as string,
        listingType: (formData.get('listing-type') ??
          defaultSearchType) as SearchType,
        radius: formData.get('radius') as string,
        triggeredByRemoteLocationChange:
          options.triggeredByRemoteLocationChange,
      });

      closeDropdownFromElementWithin(form);
    },
    [defaultSearchType, onSearch],
  );

  const [searchTypeForPopularSearches, setSearchTypeForPopularSearches] =
    useKeyedState(defaultSearchType, { key: defaultSearchType });

  const contextValue: DesktopSearchBarContextValue = useMemo(
    () => ({
      defaultSearchType,
      submit,
      searchTypeForPopularSearches,
      setSearchTypeForPopularSearches,
    }),
    [
      defaultSearchType,
      searchTypeForPopularSearches,
      setSearchTypeForPopularSearches,
      submit,
    ],
  );

  return (
    <DesktopSearchBarInputsForm
      id="sticky-desktop-search-bar"
      ref={formRef}
      onSubmit={(event) => {
        event.preventDefault();
        submit({ triggeredByRemoteLocationChange: false });
      }}
      data-qa-id="search-inputs"
    >
      <DesktopSearchBarContext value={contextValue}>
        <DesktopSearchBarFieldsHolder>
          {searchTypeSelect}
          {queryInput}
          {locationInput}
          {radiusInput}
        </DesktopSearchBarFieldsHolder>

        {submitButton}
      </DesktopSearchBarContext>
    </DesktopSearchBarInputsForm>
  );
}
